/**
 * useOverviewEditState — shared plumbing for any surface that hosts
 * `OverviewSection`.
 *
 * `OverviewSection` enters edit mode internally and emits a `SaveBarState`
 * via `onSaveStateChange`. This hook captures that state and wires it into
 * the unsaved-changes guard, so callers only have to:
 *
 *   1. Pass `setSaveBarState` to `<OverviewSection onSaveStateChange={…}>`
 *   2. Render `<SaveBar … />` somewhere with `relative` ancestry
 *   3. Render `<UnsavedChangesModal {…modalProps} />`
 *
 * Used by both `SingleSlideout` (the entity slideout) and
 * `CRMObjectFullPageView` (the full-page entity view) so both surfaces
 * share the same floating save-bar UX and the same navigation protection.
 *
 * Modal-like surfaces (e.g. the slideout) can opt out of router/browser
 * guarding via the `blockRouterNavigation`/`blockBrowserClose` options
 * when an outer guard already handles those concerns.
 */

import { useCallback, useRef, useState } from 'react';

import {
  type UnsavedChangesModalProps,
  useUnsavedChangesGuard,
} from '@/hooks/useUnsavedChangesGuard';

import type { SaveBarState } from '../types';

interface UseOverviewEditStateOptions {
  /** Block React Router navigation when unsaved edits exist. Default: true.
   *  Set false in modal-like surfaces (e.g. slideout) whose parent already
   *  guards navigation through an outer `useUnsavedChangesGuard`. */
  blockRouterNavigation?: boolean;
  /** Block browser close/refresh when unsaved edits exist. Default: true.
   *  Same caveat — set false when an outer guard owns this. */
  blockBrowserClose?: boolean;
}

export interface UseOverviewEditStateResult {
  /** SaveBarState emitted by OverviewSection's edit mode. Pass into
   *  `<OverviewSection onSaveStateChange={setSaveBarState}>` and render
   *  `<SaveBar … />` from this when non-null. */
  saveBarState: SaveBarState | null;
  setSaveBarState: (state: SaveBarState | null) => void;
  /** True iff there are pending edits. */
  hasUnsavedChanges: boolean;
  /** Guard fn — wraps dismissive actions (close, navigate). Returns true
   *  to let the action proceed, false when it intercepted with a modal. */
  guard: (action: () => void) => boolean;
  /** Spread onto `<UnsavedChangesModal {…modalProps} />`. */
  modalProps: UnsavedChangesModalProps;
}

export function useOverviewEditState({
  blockRouterNavigation = true,
  blockBrowserClose = true,
}: UseOverviewEditStateOptions = {}): UseOverviewEditStateResult {
  const [saveBarState, setSaveBarState] = useState<SaveBarState | null>(null);
  const hasUnsavedChanges = (saveBarState?.changeCount ?? 0) > 0;

  // Stable ref so guard identity doesn't churn on every keystroke (every
  // typed character re-emits a new SaveBarState through OverviewEditSection).
  const saveBarStateRef = useRef(saveBarState);
  saveBarStateRef.current = saveBarState;

  const handleDiscard = useCallback(() => {
    saveBarStateRef.current?.onCancel();
  }, []);

  // onSaveGuarded throws on failure so the modal stays open — preferred over
  // the swallowing onSave when wired into the guard's save flow.
  const handleSaveGuarded = saveBarState?.onSaveGuarded;

  const { guard, modalProps } = useUnsavedChangesGuard({
    hasUnsavedChanges,
    onDiscard: handleDiscard,
    onSave: handleSaveGuarded,
    blockRouterNavigation,
    blockBrowserClose,
  });

  return {
    saveBarState,
    setSaveBarState,
    hasUnsavedChanges,
    guard,
    modalProps,
  };
}
